import { Dispatch, RefObject, SetStateAction, useCallback, useEffect, useRef, useState } from "react";

export interface DragResizeOptions {
	storageKey: string;
	defaultWidth: number;
	minWidth: number;
	maxWidth: number;
	direction?: "left" | "right"; // Direction to resize from (default: "right")
	// External state (optional) - if provided, hook will use these instead of internal state
	width?: number;
	setWidth?: Dispatch<SetStateAction<number>>;
	panelRef?: RefObject<HTMLDivElement>;
}

/**
 * Hook for managing drag-to-resize panel width with localStorage persistence.
 * Optimized with RAF throttling. Saves to localStorage only when drag ends.
 *
 * Supports both internal and external state management:
 * - Internal: Hook manages width state and ref (legacy mode)
 * - External: Parent provides width, setWidth, and panelRef as props
 *
 * @param options - Configuration options for resize behavior
 * @returns Object containing width, drag state, handlers, and ref
 */
export function usePersistedDragResize(options: DragResizeOptions) {
	const {
		storageKey,
		defaultWidth,
		minWidth,
		maxWidth,
		direction = "right",
		width: externalWidth,
		setWidth: externalSetWidth,
		panelRef: externalPanelRef,
	} = options;

	// Internal state (used only if external state not provided)
	const [internalWidth, internalSetWidth] = useState<number>(() => {
		if (externalWidth !== undefined) return externalWidth;
		try {
			const saved = localStorage.getItem(storageKey);
			return saved ? parseInt(saved, 10) : defaultWidth;
		} catch {
			return defaultWidth;
		}
	});

	const internalPanelRef = useRef<HTMLDivElement>(null);

	// Use external state if provided, otherwise use internal
	const width = externalWidth ?? internalWidth;
	const setWidth = externalSetWidth ?? internalSetWidth;
	const panelRef = externalPanelRef ?? internalPanelRef;

	const [isDragging, setIsDragging] = useState(false);
	const rafIdRef = useRef<number | null>(null);
	const widthRef = useRef<number>(width);
	const hasRestoredRef = useRef<boolean>(false);

	// Restore width from localStorage when using external state
	useEffect(() => {
		if (externalSetWidth && panelRef.current && !hasRestoredRef.current) {
			hasRestoredRef.current = true;
			try {
				const saved = localStorage.getItem(storageKey);
				if (saved) {
					const restoredWidth = parseInt(saved, 10);
					externalSetWidth(restoredWidth);
				}
			} catch {
				// Ignore errors, use default width
			}
		}
	}, [externalSetWidth, panelRef, storageKey]);

	// Keep widthRef in sync with width state
	useEffect(() => {
		widthRef.current = width;
	}, [width]);

	// Throttled resize handler using RAF
	useEffect(() => {
		if (!isDragging) return;

		const handleMouseMove = (e: MouseEvent) => {
			if (rafIdRef.current) return; // Skip if already scheduled

			rafIdRef.current = requestAnimationFrame(() => {
				rafIdRef.current = null;
				if (!panelRef.current) return;
				const rect = panelRef.current.getBoundingClientRect();

				const newWidth = direction === "right" ? rect.right - e.clientX : e.clientX - rect.left;

				setWidth(Math.max(minWidth, Math.min(maxWidth, newWidth)));
			});
		};

		const handleMouseUp = () => {
			setIsDragging(false);
			if (rafIdRef.current) {
				cancelAnimationFrame(rafIdRef.current);
				rafIdRef.current = null;
			}
			// Save to localStorage when drag ends
			localStorage.setItem(storageKey, widthRef.current.toString());
		};

		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", handleMouseUp);

		return () => {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
			if (rafIdRef.current) {
				cancelAnimationFrame(rafIdRef.current);
			}
		};
	}, [isDragging, direction, minWidth, maxWidth, storageKey]);

	const handleMouseDown = useCallback(() => {
		setIsDragging(true);
	}, []);

	return {
		width,
		isDragging,
		panelRef,
		handleMouseDown,
	};
}
